import { apiService } from './api';

export interface AcaoResponse {
    acao_base_id: string;
    titulo: string;
    descricao: string;
    score: number;
    status: string;
    acao_id: string;
    usuario_responsavel_id: string;
    usuario_responsavel_nome: string;
    item_id: string;
    dt_criacao: string;
}

export interface AcaoHistoricoAnexoResponse {
    anexoId: string;
    anexo: string;
    anexoUrl: string;
}

export interface AcaoHistoricoResponse {
    acaoHistoricoId: string;
    acao_id: string;
    type: string;
    comentario?: string;
    anexos: AcaoHistoricoAnexoResponse[];
    dtCriacao: string;
    criadoPor: string;
    criadoPorNome: string;
}

export interface CriarAcaoRequest {
    titulo: string;
    descricao: string;
    responsavelId?: string;
    dataExpiracao?: string;
}

export interface AtualizarAcaoRequest {
    titulo: string;
    descricao: string;
    responsavelId?: string;
    dataExpiracao?: string;
}

export interface DownloadArquivoResponse {
    download_url: string;
    expires_in_minutes: number;
}

export const actionService = {
    async getActionsByItem(itemId: string): Promise<AcaoResponse[]> {
        try {
            const url = `/acao/${itemId}/usuario`;
            const response = await apiService.get<AcaoResponse[]>(url);
            return response;
        } catch (error) {
            console.error('Error fetching actions for item:', error);
            throw error;
        }
    },

    // Create a new action for a specific item
    async createAction(itemId: string, request: CriarAcaoRequest): Promise<AcaoResponse> {
        try {
            const response = await apiService.post<AcaoResponse>(`/acao/${itemId}`, request);
            return response;
        } catch (error) {
            console.error('Error creating action:', error);
            throw error;
        }
    },

    // Update an existing action
    async updateAction(acaoId: string, request: AtualizarAcaoRequest): Promise<AcaoResponse> {
        try {
            const response = await apiService.put<AcaoResponse>(`/acao/${acaoId}`, request);
            return response;
        } catch (error) {
            console.error('Error updating action:', error);
            throw error;
        }
    },

    async getActionHistory(acaoId: string): Promise<AcaoHistoricoResponse[]> {
        try {
            const url = `/acao/${acaoId}/history`;
            const response = await apiService.get<AcaoHistoricoResponse[]>(url);
            return response;
        } catch (error) {
            console.error('Error fetching action history:', error);
            throw error;
        }
    },

    async moveAction(acaoId: string, status?: string, comentario?: string, arquivos?: File[]): Promise<AcaoResponse> {
        try {
            const url = `/acao/${acaoId}/mover`;

            const formData = new FormData();

            if (status) {
                formData.append('status', status);
            }

            if (comentario) {
                formData.append('comentario', comentario);
            }

            if (arquivos && arquivos.length > 0) {
                arquivos.forEach((arquivo, index) => {
                    formData.append(`arquivo`, arquivo);
                });
            }

            const response = await apiService.put<AcaoResponse>(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response;
        } catch (error) {
            console.error('Error moving action:', error);
            throw error;
        }
    },

    // Get download URL for action history file
    async getDownloadUrl(acaoHistoricoId: string): Promise<DownloadArquivoResponse> {
        try {
            const url = `/acao/historico/${acaoHistoricoId}/download`;
            const response = await apiService.get<DownloadArquivoResponse>(url);
            return response;
        } catch (error) {
            console.error('Error getting download URL for action history:', error);
            throw error;
        }
    },

    // Get SAS download URL for a specific attachment
    async getAnexoDownloadUrl(anexoId: string): Promise<DownloadArquivoResponse> {
        try {
            const url = `/acao/historico/anexo/${anexoId}/download`;
            const response = await apiService.get<DownloadArquivoResponse>(url);
            return response;
        } catch (error) {
            console.error('Error getting download URL for anexo:', error);
            throw error;
        }
    }
};
